import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { clearToast } from '../redux/slices/toastSlice'

/**
 * Custom hook to handle pending toasts from Redux store
 * Call this hook in components where you want to display cross-page toasts
 * 
 * @param {Object} options Configuration options
 * @param {boolean} options.autoShow Whether to automatically show toasts (default: true)
 * @param {boolean} options.clearAfterShow Whether to clear toasts after showing (default: true)
 * @returns {Object} Toast utilities
 */
export const usePendingToasts = (options = {}) => {
  const { autoShow = true, clearAfterShow = true } = options
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const pendingToasts = useSelector((state) => state.toast.pendingToasts)

  useEffect(() => {
    if (!autoShow || pendingToasts.length === 0) return

    pendingToasts.forEach((toastItem) => {
      // Determine the message to display
      let message = toastItem.message
      if (toastItem.messageId) {
        message = formatMessage(
          { id: toastItem.messageId },
          toastItem.values || {}
        )
      }

      // Show the appropriate toast type
      switch (toastItem.type) {
        case 'success':
          toast.success(message)
          break
        case 'error':
          toast.error(message)
          break
        case 'info':
          toast.info(message)
          break
        case 'warning':
          toast.warn(message)
          break
        default:
          toast(message)
      }

      // Clear the toast from Redux store if specified
      if (clearAfterShow) {
        dispatch(clearToast(toastItem.id))
      }
    })
  }, [pendingToasts, autoShow, clearAfterShow, dispatch, formatMessage])

  const showPendingToasts = () => {
    pendingToasts.forEach((toastItem) => {
      let message = toastItem.message
      if (toastItem.messageId) {
        message = formatMessage(
          { id: toastItem.messageId },
          toastItem.values || {}
        )
      }

      switch (toastItem.type) {
        case 'success':
          toast.success(message)
          break
        case 'error':
          toast.error(message)
          break
        case 'info':
          toast.info(message)
          break
        case 'warning':
          toast.warn(message)
          break
        default:
          toast(message)
      }

      dispatch(clearToast(toastItem.id))
    })
  }

  const clearAllPending = () => {
    pendingToasts.forEach((toastItem) => {
      dispatch(clearToast(toastItem.id))
    })
  }

  return {
    pendingToasts,
    showPendingToasts,
    clearAllPending,
    hasPendingToasts: pendingToasts.length > 0,
  }
}